import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import EmptyState from "@/components/admin/EmptyState";
import { StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

export default function CustomerNotesTab({ customerId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/customers/${customerId}/notes?page=1&page_size=100`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [customerId]);

  const add = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      await api.post(`/admin/customers/${customerId}/notes`, { body: trimmed });
      setBody("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note? This action is logged.")) return;
    try {
      await api.delete(`/admin/customers/${customerId}/notes/${noteId}`);
      toast.success("Note deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="customer-tab-notes" className="space-y-4">
      <div className="bg-white border border-brand-linen p-4">
        <div className="overline mb-2">Add internal note</div>
        <Textarea
          data-testid="note-composer-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Visible to admins only. Plain text."
          rows={3}
          maxLength={4000}
        />
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-brand-muted">{body.length}/4000</div>
          <Button
            data-testid="note-composer-submit"
            onClick={add}
            disabled={posting || body.trim().length === 0}
          >
            {posting ? "Saving…" : "Add note"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-brand-linen">
        <div className="px-4 py-3 border-b border-brand-linen flex items-center justify-between">
          <div className="overline">Internal notes ({total})</div>
          {loading && <div className="text-xs text-brand-muted">Loading…</div>}
        </div>
        {items.length === 0 && !loading ? (
          <EmptyState
            icon={StickyNote}
            title="No notes yet"
            caption="Notes are visible only to Owner and Admins."
            testId="customer-notes-empty"
          />
        ) : (
          <ul className="divide-y divide-brand-linen/50" data-testid="customer-notes-list">
            {items.map((n) => (
              <li key={n.id} className="px-4 py-3 flex gap-4" data-testid={`note-row-${n.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-brand-ink whitespace-pre-wrap break-words">{n.body}</div>
                  <div className="text-xs text-brand-muted mt-1">
                    {n.author_email}
                    <span className="opacity-50"> · {n.author_role || "admin"} · {relTime(n.created_at)}</span>
                  </div>
                </div>
                <button
                  data-testid={`note-delete-${n.id}`}
                  onClick={() => remove(n.id)}
                  className="text-brand-muted hover:text-destructive transition-colors"
                  title="Delete note"
                  aria-label="Delete note"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
